import {
  useState, useEffect, useCallback, createContext, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IFileWithMeta } from 'react-dropzone-uploader/dist/Dropzone';
import uuid from 'uuid';
import { useParams } from 'react-router';
import { UpChunk } from '@mux/upchunk';
import downloadJs from 'downloadjs';

import { useForceUpdate } from '../../../hooks/index';
import { selectUploadMedia } from '../../../store/modules/articles/selectors';
import {
  uploadArticleMediaSuccess,
  removeUploadArticleMedia,
  deleteArticleRequest,
  uploadNewArticleMediaRequest,
  uploadNewArticleVideoRequest,
  removeDownloadMedia,
  removeAllDownloadMedia,
  removeAllUploadArticleMedia,
} from '../../../store/modules/articles/actions';
import { otherVideoTypes } from '../../../utility/helpers';

/* eslint-disable no-param-reassign */

interface UploadContextType {
  files: FileMeta[];
  downloadFiles: DownloadFile[];
  restartDownloadFile: (file: DownloadFile) => void;
  addFile: (props: FileMeta) => void;
  remove: (file: FileMeta) => void;
  removeDownloadFile: (file: DownloadFile) => void;
  removeAllFiles: () => void;
  cancel: (file: FileMeta) => void;
  cancelDownloadFile: (file: DownloadFile) => void;
  cancelAllFiles: () => void;
  restart: (file: FileMeta) => void;
}

const initialState = {
  files: [],
  downloadFiles: [],
  addFile: () => {},
  remove: () => {},
  removeDownloadFile: () => {},
  removeAllFiles: () => {},
  cancel: () => {},
  cancelDownloadFile: () => {},
  cancelAllFiles: () => {},
  restart: () => {},
  restartDownloadFile: () => {},
};

type UploadHandler = {
  url: string;
  file: FileMeta;
};

export type FileMeta = IFileWithMeta & {
  upChunk?: UpChunk;
  isChunk?: boolean;
};

export type DownloadHandler = {
  url: string;
  name: string;
};

export type DownloadFile = DownloadHandler & {
  xhr?: XMLHttpRequest;
  percent: number;
  status: string;
  downloadId: string;
};

const MAX_VIDEO_UPLOADING_NUMBER = 2;
const MAX_DOCUMENTS_UPLOADING_NUMBER = 2;

export const UploadContext = createContext<UploadContextType>(initialState);

// 10MB chunk size offers about 2* speed increase, over 1MB chunk size, for larger sized (e.g. 80MB)
const MbToBytes = 1024;
const minChunkSize = 5 * MbToBytes; // Minimum chunk size = 1MB
const maxChunkSize = 20 * MbToBytes; // Max chunk size = 20MB
const targetChunkCount = 10; // 10 chunks, 10% each
const chunkSizeMultipleRequirement = 256; // chunk sizes must be a multiple of this number

export const useUploadFiles = (): UploadContextType => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const [files, setStateFiles] = useState<FileMeta[]>([]);
  const [downloadFiles, setDownloadFiles] = useState<DownloadFile[]>([]);
  const { uploads, downloads } = useSelector(selectUploadMedia);
  const { id: projectId } = useParams<{ id: string }>();
  const [doneDocumentFiles, setDoneDocumentFiles] = useState<number>(0);
  const [doneVideoFiles, setDoneVideoFiles] = useState<number>(0);

  const uploadingDocuments = useMemo<FileMeta[]>(() => {
    const documents: FileMeta[] = [];
    const process = files.filter((file) => file.meta.status === 'uploading' && !file.isChunk);
    const notDone = files.filter((file) => file.meta.status === 'ready'  && !file.isChunk);

    if(!files.length || process.length >= 2) {
      return documents;
    }

    for(let i = 0; i < (MAX_DOCUMENTS_UPLOADING_NUMBER - process.length); i++ ){
      if (notDone[i]) {
        notDone[i].meta.status = 'preparing';
        documents.push(notDone[i]);
      }
    }

    return documents;
    // eslint-disable-next-line
  }, [files, doneDocumentFiles]);


  useEffect(()=>{
    if(uploadingDocuments.length){
      uploadingDocuments.forEach((file)=>{
        const data = {
          title: file.meta.name,
          projectId: parseInt(projectId, 10),
          uploadId: file.meta.id,
        };

        dispatch(uploadNewArticleMediaRequest(data));
      })
    }
  },[uploadingDocuments, projectId, dispatch])


  const uploadingVideos = useMemo<FileMeta[]>(() => {
    const videos: FileMeta[] = [];
    const process = files.filter((file) => file.meta.status === 'uploading' && file.isChunk);
    const notDone = files.filter((file) => file.meta.status === 'ready'  && file.isChunk);

    if(!files.length || process.length >= 2) {
      return videos;
    }

    for(let i = 0; i < (MAX_VIDEO_UPLOADING_NUMBER - process.length); i++ ){
      if (notDone[i]) {
        notDone[i].meta.status = 'preparing';
        videos.push(notDone[i]);
      }
    }

    return videos;
    // eslint-disable-next-line
  }, [files, doneVideoFiles]);

  useEffect(()=>{
    if(uploadingVideos.length){
      uploadingVideos.forEach((file)=>{
        const data = {
          title: file.meta.name,
          projectId: parseInt(projectId, 10),
          uploadId: file.meta.id,
        };

        dispatch(uploadNewArticleVideoRequest(data));
      })
    }
  },[uploadingVideos, projectId, dispatch])

  const addDoneVideoFile = useCallback((id: string) => {
    const foundFile = files.find((item) => item.meta.id === id);
    if(!foundFile) {
      return;
    }

    if(foundFile.isChunk){
      setDoneVideoFiles(doneVideoFiles + 1);
    }
  }, [ doneVideoFiles, files]);

  const addDoneDocumentFile = useCallback((id: string) => {
    const foundFile = files.find((item) => item.meta.id === id);
    if(!foundFile) {
      return;
    }

    if(!(foundFile.meta.type.startsWith('video/') || otherVideoTypes(foundFile.file.name))){
      setDoneDocumentFiles(doneDocumentFiles + 1);
    }
  }, [ doneDocumentFiles, files]);

  const uploadFile = useCallback(({ url, file }: UploadHandler) => {
    const method = 'put';
    const xhr = new XMLHttpRequest();

    xhr.open(method, url, true);

    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('Content-Type', file.file.type);

    // update prgress (can be used to show progress indicator)
    xhr.upload.addEventListener('progress', (e) => {
      let percent = (e.loaded * 100.0) / e.total;
      percent = percent >= 99 ? 99 : percent;
      file.meta.percent = percent;
      forceUpdate();
    });

    xhr.addEventListener('readystatechange', () => {
      if (xhr.readyState !== 2 && xhr.readyState !== 4) return;

      if (xhr.status === 0 && file.meta.status !== 'aborted') {
        file.meta.status = 'exception_upload';
        forceUpdate();
      }

      if (xhr.status > 0 && xhr.status < 400) {
        if (xhr.readyState === 2) file.meta.status = 'headers_received';
        if (xhr.readyState === 4) {
          file.meta.percent = 100;
          file.meta.status = 'done';
          addDoneDocumentFile(file.meta.id);
          dispatch(uploadArticleMediaSuccess(file.meta.id));
        }
        forceUpdate();
      }

      if (xhr.status >= 400 && file.meta.status !== 'error_upload') {
        file.meta.status = 'error_upload';
        window.analytics.track('document.upload.error', {document_id: file.meta.id});
        forceUpdate();
      }
    });
    xhr.send(file.file);
    file.xhr = xhr;
    file.meta.status = 'uploading';
    forceUpdate();
  }, [forceUpdate, dispatch, addDoneDocumentFile]);

  const uploadFileChunk = useCallback(({ url, file }: UploadHandler) => {
    let dynamicChunkSize = minChunkSize;
    if (file.file.size) {
      dynamicChunkSize = (file.file.size / targetChunkCount) / MbToBytes;
      dynamicChunkSize = Math.max(minChunkSize, dynamicChunkSize);
      dynamicChunkSize = Math.min(maxChunkSize, dynamicChunkSize);

      // Force to integer
      dynamicChunkSize = Math.floor(dynamicChunkSize);

      // Chunk size must be multiples of 256
      const chunkSizeAdjustment = chunkSizeMultipleRequirement - (dynamicChunkSize % chunkSizeMultipleRequirement);
      dynamicChunkSize += chunkSizeAdjustment;
    }

    const upChunk = new UpChunk({
      endpoint: url,
      file: file.file,
      chunkSize: dynamicChunkSize,
    });

    // subscribe to events
    upChunk.on('error', () => {
      file.meta.status = 'error_upload';
      window.analytics.track('video.upload.error', {video_id: file.meta.id});
      forceUpdate();
    });

    upChunk.on('progress', ({ detail: progress }) => {
      file.meta.percent = progress;
      forceUpdate();
    });
    upChunk.on('success', () => {
      file.meta.status = 'done';
      dispatch(uploadArticleMediaSuccess(file.meta.id));
      addDoneVideoFile(file.meta.id);
      forceUpdate();
    });
    file.meta.status = 'uploading';
    file.upChunk = upChunk;
    forceUpdate();
  }, [dispatch, forceUpdate, addDoneVideoFile]);

  const handleRemove = useCallback((file: FileMeta) => {
    const index = files.findIndex((f) => f.meta.id === file.meta.id);
    if (index !== -1) {
      URL.revokeObjectURL(file.meta.previewUrl || '');
      file.meta.status = 'removed';
      files.splice(index, 1);
      setStateFiles([...files]);
      dispatch(removeUploadArticleMedia(file.meta.id));
    }
  }, [files, setStateFiles, dispatch]);

  const removeAllFiles = useCallback(() => {
    dispatch(removeAllUploadArticleMedia());
    dispatch(removeAllDownloadMedia());
    setStateFiles([]);
    setDownloadFiles([]);
  }, [dispatch, setStateFiles, setDownloadFiles]);

  const handleCancel = useCallback((file: FileMeta) => {
    if (file.meta.status !== 'uploading') {
      return;
    }
    if (file.xhr) {
      file.meta.status = 'aborted';
      file.xhr.abort();
    }
    if (file.upChunk) {
      file.upChunk.pause();
    }
    const fileByList = uploads.find((item) => item.uploadId === file.meta.id);
    if (fileByList) {
      dispatch(deleteArticleRequest(fileByList.article.id));
    }
    forceUpdate();
    handleRemove(file);
  }, [forceUpdate, uploads, dispatch, handleRemove]);

  const cancelAllFiles = useCallback(() => {
    files.forEach((item) => {
      if (item.xhr) {
        item.xhr.abort();
      }
      if (item.upChunk) {
        item.upChunk.pause();
      }
      const fileByList = uploads.find((media) => media.uploadId === item.meta.id && item.meta.status !== 'done');
      if (fileByList) {
        dispatch(deleteArticleRequest(fileByList.article.id));
      }
    });
    downloadFiles.forEach((item) => {
      if (item.xhr) {
        item.xhr.abort();
      }
    });
    removeAllFiles();
  }, [dispatch, files, downloadFiles, uploads, removeAllFiles]);

  const handleRestart = useCallback((file: FileMeta) => {
    const fileByList = uploads.find((item) => item.uploadId === file.meta.id);
    if (fileByList && fileByList.signedUrl) {
      file.meta.status = 'restarted';
      file.meta.percent = 0;
      uploadFile({ file, url: fileByList.signedUrl.url });
      forceUpdate();
    }
  }, [uploadFile, uploads, forceUpdate]);

  const addFile = useCallback((props: FileMeta) => {
    const file = { ...props };
    const isVideo = file.meta.type.startsWith('video/') || otherVideoTypes(file.file.name);
    file.isChunk = isVideo;
    file.meta.id = `upload-file-${uuid()}`;
    file.meta.status = 'ready';
    files.push(file);
    setStateFiles([...files]);
  }, [ files, setStateFiles]);

  const removeDownloadFile = useCallback((file: DownloadFile) => {
    const index = downloadFiles.findIndex((item) => item.downloadId === file.downloadId);
    if (index !== -1) {
      downloadFiles.splice(index, 1);
      setDownloadFiles([...downloadFiles]);
      dispatch(removeDownloadMedia(file.downloadId));
    }
  }, [dispatch, downloadFiles, setDownloadFiles]);

  const downloadFile = useCallback((data: DownloadFile) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', data.url, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      data.status = 'done';
      downloadJs(xhr.response, data.name);
      dispatch(removeDownloadMedia(data.downloadId));
      forceUpdate();
    };
    xhr.onerror = () => {
      data.status = 'error';
      forceUpdate();
    };
    xhr.onprogress = (e) => {
      data.percent = (e.loaded * 100.0) / e.total;
      forceUpdate();
    };
    data.xhr = xhr;

    xhr.send();
    forceUpdate();
  }, [forceUpdate, dispatch]);

  const cancelDownloadFile = useCallback((file: DownloadFile) => {
    if (file.status !== 'downloading') {
      return;
    }
    if (file.xhr) {
      file.status = 'aborted';
      file.xhr.abort();
    }
    forceUpdate();
  }, [forceUpdate]);

  const restartDownloadFile = useCallback((file: DownloadFile) => {
    const currentDownloadFile = downloadFiles.find(
      (item) => item.downloadId === file.downloadId,
    );
    if (currentDownloadFile) {
      currentDownloadFile.status = 'downloading';
      currentDownloadFile.percent = 0;
      currentDownloadFile.xhr?.send();
      forceUpdate();
    }
  }, [downloadFiles, forceUpdate]);

  useEffect(() => {
    if(uploads.length){
      uploads.forEach((item) => {
        const file = files.find((f) => item.uploadId === f.meta.id);
        if (file) {
          const { status } = file.meta;
          if (!['uploading', 'done', 'error_upload', 'exception_upload', 'aborted'].includes(status)) {
            if (item.signedUrl) {
              if (file.isChunk) {
                uploadFileChunk({
                  url: item.signedUrl.url,
                  file,
                });
              } else {
                uploadFile({
                  url: item.signedUrl.url,
                  file,
                });
              }
            }
          }
        }
      });
    }
  }, [files, uploadFile, uploadFileChunk, handleCancel, handleRemove, handleRestart, uploads]);

  useEffect(() => {
    downloads.forEach((item) => {
      const downloadMedia = downloadFiles.find((findItem) => findItem.downloadId === item.downloadId);
      if (downloadMedia === undefined) {
        const newDownloadMedia = {
          name: item.name,
          url: item.url,
          downloadId: item.downloadId,
          percent: 0,
          status: 'requested',
        };
        if (item.urlRequested) {
          downloadFiles.push(newDownloadMedia);
        } else {
          newDownloadMedia.status = 'downloading';
          downloadFiles.push(newDownloadMedia);
          downloadFile(newDownloadMedia);
        }
        setDownloadFiles([...downloadFiles]);
      } else if (downloadMedia.status === 'requested' && !item.urlRequested && item.url) {
        downloadMedia.url = item.url;
        downloadMedia.status = 'downloading';
        downloadFile(downloadMedia);
        setDownloadFiles([...downloadFiles]);
      }
    });
  }, [downloads, downloadFile, downloadFiles, setDownloadFiles]);

  return {
    files,
    addFile,
    remove: handleRemove,
    removeAllFiles,
    cancelAllFiles,
    cancel: handleCancel,
    restart: handleRestart,
    removeDownloadFile,
    cancelDownloadFile,
    downloadFiles,
    restartDownloadFile,
  };
};
